import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { useTranslation } from "@/i18n/useTranslation";
import { TaskCard } from "./TaskCard";
import { Task } from "./boardTypes";
import { 
  attachClosestEdge, 
  extractClosestEdge,
  type Edge 
} from '@atlaskit/pragmatic-drag-and-drop-hitbox/closest-edge';
import { DropIndicator } from '@atlaskit/pragmatic-drag-and-drop-react-drop-indicator/box';
import { combine } from '@atlaskit/pragmatic-drag-and-drop/combine';
import { draggable, dropTargetForElements } from '@atlaskit/pragmatic-drag-and-drop/element/adapter';
import { triggerPostMoveFlash } from '@atlaskit/pragmatic-drag-and-drop-flourish/trigger-post-move-flash';
import { token } from '@atlaskit/tokens';
import { useBoardContext } from './BoardContext';
import { ColumnContext } from './ColumnContext';

interface BoardColumnProps {
  column: {
    id: string;
    title: string;
    taskIds: string[];
  };
  tasks: { [key: string]: Task };
  index: number;
  getFilteredTasks: (taskIds: string[]) => string[];
  onEditTask?: (task: Task) => void;
}

// Column state types
type State =
  | { type: 'idle' }
  | { type: 'is-card-over' }
  | { type: 'is-task-over'; closestEdge: Edge | null };

// Stable state objects for performance
const idle: State = { type: 'idle' };
const isCardOver: State = { type: 'is-card-over' };

export const BoardColumn = memo(function BoardColumn({
  column,
  tasks,
  index,
  getFilteredTasks,
  onEditTask
}: BoardColumnProps) {
  const { t } = useTranslation();
  const filteredTaskIds = getFilteredTasks(column.taskIds);
  const boardTasks = filteredTaskIds.map(taskId => tasks[taskId]);

  // Refs for DOM elements
  const columnRef = useRef<HTMLDivElement>(null);
  const columnInnerRef = useRef<HTMLDivElement>(null);
  const headerRef = useRef<HTMLDivElement>(null);
  const scrollableRef = useRef<HTMLDivElement>(null);
  
  // State for drag and drop
  const [state, setState] = useState<State>(idle);
  const [isDragging, setIsDragging] = useState<boolean>(false);

  // Get context
  const { instanceId, registerColumn } = useBoardContext();

  // Set up the column
  useEffect(() => {
    if (!columnRef.current || !columnInnerRef.current || !scrollableRef.current) return;

    return combine(
      // Register column
      registerColumn({
        columnId: column.id,
        entry: {
          element: columnRef.current
        }
      }),

      // Make column a drop target for cards
      dropTargetForElements({
        element: columnInnerRef.current,
        getData: () => ({ columnId: column.id }),
        canDrop: ({ source }) => {
          return source.data.instanceId === instanceId && source.data.type === 'card';
        },
        getIsSticky: () => true,
        onDragEnter: () => setState(isCardOver),
        onDragLeave: () => setState(idle),
        onDrop: (args) => {
          setState(idle);
          if (args.source.data.type === 'card') {
            triggerPostMoveFlash(columnInnerRef.current!);
          }
        }
      }),
    );
  }, [column.id, registerColumn, instanceId]);

  // Keep stable references to tasks for performance
  const stableTasks = useRef(filteredTaskIds);
  useEffect(() => {
    stableTasks.current = filteredTaskIds;
  }, [filteredTaskIds]);

  // Helper methods for ColumnContext
  const getTaskIndex = useCallback((taskId: string) => {
    return stableTasks.current.indexOf(taskId);
  }, []);

  const getNumTasks = useCallback(() => {
    return stableTasks.current.length;
  }, []);

  // Column context value
  const contextValue = useMemo(() => {
    return { columnId: column.id, getTaskIndex, getNumTasks };
  }, [column.id, getTaskIndex, getNumTasks]);

  return (
    <ColumnContext.Provider value={contextValue}>
      <Card
        ref={columnRef}
        className={`bg-muted/20 p-2 h-full flex flex-col transition-colors
          ${state.type === 'is-card-over' ? 'bg-primary/10 ring-1 ring-primary/20' : ''}`}
        data-column-id={column.id}
      >
        <div
          ref={headerRef}
          className="p-2 font-medium text-gray-700 mb-2 flex justify-between items-center"
        >
          <div className="flex items-center gap-2">
            {t(`board.${column.title}`)}
            <Badge variant="outline" className="ml-2">
              {filteredTaskIds.length}
            </Badge>
          </div>
        </div>

        <div
          ref={columnInnerRef}
          className={`relative min-h-[150px] transition-colors flex-1 overflow-y-auto
            ${state.type === 'is-card-over' ? 'bg-primary/5' : ''}`}
          data-column-id={column.id}
        >
          <div ref={scrollableRef} className="h-full">
            {/* Empty column placeholder */}
            {boardTasks.length === 0 && (
              <div className="p-3 text-sm text-muted-foreground text-center h-full flex items-center justify-center">
                <p>Drag tasks here</p>
              </div>
            )}

            {/* Tasks */}
            {boardTasks.map((task, index) => (
              <TaskCard
                key={task.id}
                task={task}
                index={index}
                onEdit={onEditTask}
              />
            ))}
          </div>
        </div>

        {/* Drop indicator for column */}
        {state.type === 'is-task-over' && state.closestEdge && (
          <DropIndicator edge={state.closestEdge} gap={token('space.200', '8px')} />
        )}
      </Card>
    </ColumnContext.Provider>
  );
});
